import React, { useEffect, useState } from 'react';
import { fetchAllOrders } from '../services/AllOrdersService';
import Sidebar from '../components/Sidebar';

const AllOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({
    order_status: '',
    evaluation_status: '',
    cwe_final_score: '',
    order_id: '',
  });

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const res = await fetchAllOrders();
        setOrders(res);
        console.log('[AllOrdersPage] Loaded all orders:', res);
      } catch (err) {
        console.error('[AllOrdersPage] Error fetching all orders:', err);
      }
    };

    loadOrders();
  }, [filters, sortConfig]); // Re-fetch orders when filters or sortConfig change

  const sortableFields = [
    'order_id', 'freelancer_id', 'task_id', 'assigned_at', 'due_date', 'submitted_at',
    'payout_date', 'project_id', 'max_payout', 'client_id', 'cwe_a2_01_formatting_score',
    'cwe_a2_02_grammar_score', 'cwe_a2_03_seo_score', 'cwe_m_01_manual_score', 'cwe_final_score'
  ];

  const dateFields = [
    'assigned_at', 'due_date', 'submitted_at', 'payout_date', 'evaluation_started_at', 
    'evaluation_completed_at', 'created_at', 'updated_at'
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredOrders = orders.filter((order) => {
    const passesOrderStatus = !filters.order_status || order.order_status === filters.order_status;
    const passesEvaluationStatus = !filters.evaluation_status || order.evaluation_status === filters.evaluation_status;
    const passesCweFinalScore = filters.cwe_final_score === ''
      ? true
      : filters.cwe_final_score === '65_and_above'
      ? order.cwe_final_score >= 65
      : filters.cwe_final_score === 'below_65'
      ? order.cwe_final_score < 65
      : true;
    const passesOrderId = !filters.order_id || order.order_id.toString().includes(filters.order_id);

    return passesOrderStatus && passesEvaluationStatus && passesCweFinalScore && passesOrderId;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;

    let aValue = a[key];
    let bValue = b[key];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const allKeys = orders[0] ? Object.keys(orders[0]) : [];

  const uniqueValues = (key) => {
    return [...new Set(orders.map((order) => order[key]).filter(Boolean))];
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 p-6 overflow-hidden flex flex-col">
        <h1 className="text-2xl font-bold mb-4">All Orders</h1>

        <div className="flex-1 overflow-auto border rounded">
          <div className="min-w-full overflow-x-auto">
            <table className="table-auto min-w-max text-sm border border-gray-300">
              <thead className="bg-gray-100 sticky top-0 z-10">
                {/* Dropdown filter row */}
                <tr>
                  <th className="px-2 py-2 border"></th>
                  {/* Move the filter box to order_id column */}
                  {allKeys.map((key) => (
                    <th key={key} className="px-2 py-1 border">
                      {key === 'order_id' && (
                        <input
                          type="text"
                          className="w-full text-xs border rounded"
                          placeholder="Filter by Order ID"
                          value={filters.order_id}
                          onChange={(e) => handleFilterChange('order_id', e.target.value)}
                        />
                      )}
                      {key === 'order_status' && (
                        <select
                          className="w-full text-xs border rounded"
                          value={filters.order_status}
                          onChange={(e) => handleFilterChange('order_status', e.target.value)}
                        >
                          <option value="">All</option>
                          {uniqueValues('order_status').map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      )}
                      {key === 'evaluation_status' && (
                        <select
                          className="w-full text-xs border rounded"
                          value={filters.evaluation_status}
                          onChange={(e) => handleFilterChange('evaluation_status', e.target.value)}
                        >
                          <option value="">All</option>
                          {uniqueValues('evaluation_status').map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      )}
                      {key === 'cwe_final_score' && (
                        <select
                          className="w-full text-xs border rounded"
                          value={filters.cwe_final_score}
                          onChange={(e) => handleFilterChange('cwe_final_score', e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="65_and_above">65 and above</option>
                          <option value="below_65">Below 65</option>
                        </select>
                      )}
                    </th>
                  ))}
                </tr>

                {/* Header row */}
                <tr>
                  <th className="px-2 py-2 whitespace-nowrap border">Sl No</th>
                  {allKeys.map((key) => (
                    <th
                      key={key}
                      className={`px-2 py-2 whitespace-nowrap border cursor-pointer ${
                        key === 'submission_text' ? 'min-w-[150px] max-w-[300px]' : 'min-w-[100px]'
                      }`}
                      onClick={() => sortableFields.includes(key) && handleSort(key)}
                      title={sortableFields.includes(key) ? 'Click to sort' : ''}
                    >
                      {key}
                      {sortConfig.key === key && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {sortedOrders.map((order, index) => (
                  <tr key={order.order_id} className="text-center border-t hover:bg-gray-50">
                    <td className="px-2 py-2 border">{index + 1}</td>
                    {allKeys.map((key) => (
                      <td
                        key={key}
                        className={`px-2 py-2 border text-xs text-left ${
                          key === 'submission_text'
                            ? 'max-w-[200px] truncate overflow-hidden hover:whitespace-normal'
                            : ''
                        }`}
                        title={
                          typeof order[key] === 'string' && order[key].length > 50
                            ? order[key]
                            : undefined
                        }
                      >
                        {order[key] === null || order[key] === undefined
                          ? 'N/A'
                          : dateFields.includes(key)
                          ? new Date(order[key]).toLocaleString()
                          : order[key].toString()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllOrdersPage;
